import { useState } from 'react'

import { Button, DialogFooter, LoadingButton } from '@fortem-monorepo/react-ui'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { useAdjustDailyAccruedInterest } from '@/react-query/mutations/accrued-interests'

import { DailyCorrectionForm } from '../forms/DailyCorrectionForm'
import { DailyCorrectionFormFields } from '../forms/DailyCorrectionFormFields'
import type { DailyCorrectionValues } from '../forms/correction.schema'
import { AccruedInterestDialogFrame } from './AccruedInterestDialogFrame'
import { CorrectionReviewTable } from './CorrectionReviewTable'

export type DailyCorrectionDialogProps = {
  params: AccruedInterests.Params
  row: AccruedInterests.DailyRow
  onClose: () => void
}

export const DailyCorrectionDialog = ({ params, row, onClose }: DailyCorrectionDialogProps) => {
  const mutation = useAdjustDailyAccruedInterest()
  const [review, setReview] = useState<DailyCorrectionValues | null>(null)
  const confirm = async () => {
    if (!review || mutation.isPending) return
    try {
      await mutation.mutateAsync({ ...params, trade_id: row.trade_id, ...review })
      onClose()
    } catch {
      // Preserve input and review; the mutation hook reports the API error.
    }
  }

  return (
    <AccruedInterestDialogFrame
      title={review ? 'Confirm daily correction' : 'Correct daily accrued interest'}
      description={`${params.fund_name} · ${params.value_date} · ${row.trade_id} · ${row.currency ?? '—'}`}
      pending={mutation.isPending}
      onClose={onClose}
    >
      <p className="text-sm text-muted-foreground">{row.unique_identifier}</p>
      <DailyCorrectionForm
        defaultValues={{ amount: row.trade_daily_accrual_amount, notes: row.notes ?? '' }}
        onSubmit={(values) => {
          if (!review && !mutation.isPending) setReview({ ...values })
        }}
      >
        {({ formState }, submit) => (
          <>
            <div hidden={!!review}>
              <DailyCorrectionFormFields disabled={!!review || mutation.isPending} />
            </div>
            {review && (
              <CorrectionReviewTable
                rows={[
                  ['Daily accrued amount', row.trade_daily_accrual_amount, review.amount],
                  ['Notes', row.notes, review.notes],
                ]}
              />
            )}
            {mutation.isError && (
              <p role="alert" className="my-3 text-sm text-destructive">
                {getErrorMessage(mutation.error)}
              </p>
            )}
            <DialogFooter className="mt-4">
              <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={onClose}>
                Cancel
              </Button>
              {review ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => setReview(null)}
                  >
                    Back
                  </Button>
                  <LoadingButton type="button" size="sm" disabled={mutation.isPending} onClick={confirm}>
                    Confirm correction
                  </LoadingButton>
                </>
              ) : (
                <LoadingButton
                  type="submit"
                  size="sm"
                  disabled={!formState.isValid || formState.isSubmitting}
                  onClick={async (event) => {
                    event.preventDefault()
                    await submit()
                  }}
                >
                  Review correction
                </LoadingButton>
              )}
            </DialogFooter>
          </>
        )}
      </DailyCorrectionForm>
    </AccruedInterestDialogFrame>
  )
}
